import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FormControl, InputLabel, MenuItem, Select } from "@mui/material";
import Navbar from "../components/Navbar";
import TourManagementSidebar from "../components/tour/TourManagementSidebar";
import { Paths } from "../router/Paths";
import useOnLoadPage from "../hooks/useOnLoadPage";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../app/store";
import { setPackageData } from "../reducer/tourReducer";
import { usePackage } from "../hooks/usePackage";
import { addActiveTab } from "../reducer/sideBarSlice";

function TourManagementPackageKeywordsPage() {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { packageData } = useSelector((state: RootState) => state.tour);
  const [keyword, setKeyword] = useState("");
  const { createPrePackage } = usePackage();
  const [loading, setLoading] = useState(false);
  const progressref = useRef(4);
  const [suggestions, setSuggestions] = useState<string[]>([
    "Architecture",
    "Culture",
    "Food",
    "History",
  ]);
  const { PrepackageDataId } = useSelector((state: RootState) => state.tour);

  useOnLoadPage({ tabActive: "create" });

  useEffect(() => {
    const handleKeyDown = (event: any) => {
      if (event.keyCode === 13) {
        handleAdd();
      }
    };

    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [keyword]);

  const onContinue = () => {
    dispatch(addActiveTab(Paths.tourManagementPackageInclusion));
    navigate(Paths.tourManagementPackageInclusion);
  };

  const onSave = async () => {
    const updatedPackageData = {
      ...packageData,
      _id: PrepackageDataId,
      progress: Number(progressref.current),
    };

    dispatch(setPackageData(updatedPackageData));
    setLoading(true);

    try {
      await createPrePackage(updatedPackageData);
      localStorage.setItem("packageData", JSON.stringify(updatedPackageData));
      navigate(Paths.welcome);
    } catch (error) {
      console.error("Error saving package:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveKeyword = (index: number) => {
    let data = packageData?.keywords ? [...packageData?.keywords] : [];
    let filteredData = data.filter((_, idx) => idx !== index);
    dispatch(
      setPackageData({
        keywords: filteredData,
      })
    );
    setKeyword("");
  };

  const handleAdd = () => {
    if (!keyword) return;
    let data = packageData?.keywords
      ? [...packageData?.keywords, keyword]
      : [keyword];
    dispatch(
      setPackageData({
        keywords: data,
      })
    );
    setKeyword("");
  };

  const handleSuggestionClick = (suggestion: string) => {
    let data = packageData?.keywords || [];
    if (!data.includes(suggestion)) {
      data = [...data, suggestion];
      dispatch(
        setPackageData({
          keywords: data,
        })
      );
    }
  };

  const handleBack = () => {
    navigate(Paths.tourManagementPackageLocation);
  };

  return (
    <div className={Classname.container}>
      <Navbar />
      <div className={Classname.content}>
        <TourManagementSidebar selected={5} />

        <div className={Classname.detail}>
          <span className={Classname.title}>
            Add keywords to your product  {" "}
            <span className="text-sm text-gray-500 font-normal">
              (optional)
            </span>
          </span>
          <p className={Classname.description}>
            Keywords work as tags for your product and help customers find it
            when they search by a theme or their interests. Try to use all 15
            for maximum reach.
          </p>
          <div className="mb-4"></div>
          <div className="flex gap-3">
            <input
              type="text"
              placeholder="Enter keyword"
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              className={Classname.input}
            />
            <button
              className="bg-primary h-14 px-4 rounded-lg flex items-center justify-center text-white font-bold mt-2"
              onClick={handleAdd}
            >
              + Add
            </button>
          </div>
          <div className={Classname.suggestionContainer}>
            {packageData?.keywords?.map((kword, index) => (
              <div
                className={Classname.keyword}
                key={index}
                onClick={() => handleRemoveKeyword(index)}
              >
                <span>{kword}</span>
                <img
                  src="/IconMinus.svg"
                  alt=""
                  className="h-5 w-5 object-contain"
                />
              </div>
            ))}
          </div>
          <span className="mt-10 text-gray-500">SUGGESTIONS</span>
          <div className={Classname.suggestionContainer}>
            {suggestions.length > 0 ? (
              suggestions.map((suggestion, index) => (
                <div
                  key={index}
                  className={Classname.suggestion}
                  onClick={() => handleSuggestionClick(suggestion)}
                >
                  {suggestion}
                </div>
              ))
            ) : (
              <p>No suggestions available.</p>
            )}
          </div>

          <div className="flex justify-between items-centers mt-10">
            <button className={Classname.saveButton2} onClick={handleBack}>
              Back
            </button>
            <div className={Classname.buttonsContainer}>
              <button
                className={Classname.saveButton}
                onClick={onSave}
                disabled={loading}
              >
                {loading ? "Please Wait..." : "Save and exit"}
              </button>
              <button className={Classname.continueButton} onClick={onContinue}>
                Continue
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default TourManagementPackageKeywordsPage;

const Classname = {
  container:
    "flex w-full min-h-screen relative bg-white flex-col items-center font-main",
  content:
    "flex flex-col lg:flex-row w-full max-w-7xl flex-1 pt-10 lg:pt-0 max-lg:px-4",
  detail: "flex flex-col py-10 lg:px-6 w-full",
  title: "font-bold text-lg mt-6 mb-2",
  input: "flex flex-1 h-14 border border-gray-400 rounded-md px-4 mt-2",
  textarea:
    "flex w-full h-44 py-4 border border-gray-400 rounded-md px-4 mt-2 mb-6",
  description: "flex text-base mb-4",
  suggestionContainer: "flex flex-wrap gap-4 mt-4",
  suggestion:
    "text-primary1 text-sm border border-primary1 px-3 py-1 rounded-md",
  keyword:
    "text-primary1 text-sm border border-primary1 px-3 py-1 rounded-md cursor-pointer flex items-center gap-2",
  buttonsContainer: "flex max-lg:justify-between gap-3 max-lg:w-full",
  highlightButton:
    "font-bold flex items-center gap-2 cursor-pointer text-primary1 py-4",
  saveButton:
    "h-12 px-6 flex items-center lg:justify-center font-bold text-gray-700 border border-gray-500 rounded-md",
  saveButton2:
    "h-12 px-6 flex items-center lg:justify-center font-bold text-gray-700 border border-gray-500 rounded-md max-lg:hidden",
  continueButton:
    "h-12 px-6 flex items-center justify-center text-white font-bold bg-primary1 rounded-md",
};
